import type { Essentials } from '@wix/fe-essentials/core';
import type { Trans, useTranslation } from '@wix/fe-essentials/react';
import type { ReportProps } from '@wix/fe-essentials/bi';
import {
  Environment,
  EnvironmentBi,
  ErrorMonitor,
  fetchTranslations,
  FocusManager,
  History,
  messagesEnImportGlobal,
  ModalLayoutProps,
  OnlineManager,
  PartialLodash,
  PartialWindow,
  ReportBI,
  Translate,
  WixPatternsMonitor,
} from '../services';
import { QueryCache } from 'react-query/core';
import {
  CloseSidePanel,
  ContextType,
  GetHostContainer,
  OnNavigation,
  OnSidePanelOpen,
  PatternsErrorHandler,
  ShowToast,
  wixPatternsEnvironment,
  WixPatternsEssentials,
  WixPatternsInitialProps,
} from '../model';
import { action, computed, IObservableArray, makeObservable } from 'mobx';
import { TaskState } from './TaskState';
import { OnlineState } from './OnlineState';
import { createComponentSettings } from '../services/createComponentSettings';
import { OptimisticPatch } from './CollectionOptimisticPatch';
import { ConditionalModalState } from './ConditionalModalState';
import { EventEmitter } from 'events';
import { fixDomainForBI, TypedEmitter } from '../util';
import { TransitionState } from './TransitionState';
import { ReactElement } from 'react';
import { createUseExperimentsCompat } from './useExperimentsCompat';
import { v4 as uuid } from 'uuid';
import { BeforeUnloadCompatState } from './BeforeUnloadCompatState';
import type { errorHandler } from '@wix/essentials';
import { PatternsLayoutType } from '../model/PatternsLayoutType';

type WixPatternsContainerEvents = TypedEmitter<{
  navigation: () => ModalLayoutProps | null | undefined;
}>;

export interface WixPatternsContainerParams {
  readonly initialProps?: WixPatternsInitialProps;
  readonly createEssentials: WixPatternsEssentials['createEssentials'];
  readonly biLogger?: WixPatternsEssentials['biLogger'];
  readonly messagesEnImport?: () => Promise<{}>;
  readonly fetchTranslations?: (p: {
    readonly errorMonitor: ErrorMonitor;
  }) => (locale: string) => Promise<{}>;
  readonly createComponentSettings?: WixPatternsEssentials['createComponentSettings'];
  readonly errorMonitor: ErrorMonitor;
  readonly httpClient: WixPatternsEssentials['httpClient'];
  readonly onlineManager: OnlineManager;
  readonly focusManager: FocusManager;
  readonly contextType?: ContextType;
  readonly history: History;
  readonly window: PartialWindow;
  readonly useTranslation: typeof useTranslation;
  readonly Trans: typeof Trans;
  readonly useExperiments: WixPatternsEssentials['useExperiments'];
  readonly experiments: WixPatternsEssentials['experiments'];
  readonly environment: WixPatternsEssentials['environment'];
  readonly environmentBI: WixPatternsEssentials['environmentBI'];
  readonly errorHandler?: WixPatternsEssentials['errorHandler'];
  readonly user: WixPatternsEssentials['user'];
  readonly showToast?: ShowToast;
  readonly usePageLocation?: WixPatternsEssentials['usePageLocation'];
  readonly navigateTo?: WixPatternsEssentials['navigateTo'];
  readonly onNavigation?: OnNavigation;
  readonly getHostContainer?: GetHostContainer;
  readonly queryCache: QueryCache;
  readonly patchesCache: Map<string, IObservableArray<OptimisticPatch<any>>>;
  readonly onSidePanelOpen?: OnSidePanelOpen;
  readonly closeSidePanel?: CloseSidePanel;
  readonly lodash: PartialLodash;
  readonly events?: WixPatternsContainerEvents;
  readonly sidePanel?: TransitionState<string>;
  readonly onNavigationModal?: ConditionalModalState<ModalLayoutProps>;
  readonly sidePanelComponents?: Map<string, ReactElement>;
  readonly appendModalsToBody?: boolean;
  readonly layoutType?: PatternsLayoutType;
  readonly useRelativeSidePanel?: boolean;
  readonly onBeforeUnload?: WixPatternsEssentials['onBeforeUnload'];
}

export class WixPatternsContainer {
  readonly createEssentials;
  readonly errorMonitor: WixPatternsContainerParams['errorMonitor'];
  readonly errorHandler: PatternsErrorHandler;
  readonly internalMonitor: WixPatternsMonitor;
  readonly internalExperiments;
  readonly experiments: WixPatternsContainerParams['experiments'];
  readonly httpClient: WixPatternsContainerParams['httpClient'];
  readonly onlineManager;
  readonly focusManager;
  readonly contextType: ContextType;
  readonly history: WixPatternsContainerParams['history'];
  readonly window: WixPatternsContainerParams['window'];
  readonly useTranslation;
  readonly Trans: typeof Trans;
  readonly useExperiments;
  readonly showToast?;
  readonly usePageLocation?;
  readonly navigateTo?;
  readonly onNavigation;
  readonly onBeforeUnload;
  readonly getHostContainer;
  readonly environment: Environment;
  readonly environmentBI: EnvironmentBi;
  readonly appendModalsToBody?: boolean;
  readonly layoutType?: string;
  readonly _useRelativeSidePanel?: boolean;

  readonly onSidePanelOpen?: OnSidePanelOpen;
  readonly closeSidePanel?: CloseSidePanel;
  readonly translate: Translate;
  readonly lodash;
  readonly i18n: Essentials['i18n'];
  readonly reportBi: ReportBI;
  readonly _messagesEnImport;
  readonly _fetchTranslations;

  queryCache: QueryCache;
  readonly patchesCache;

  readonly initTask = new TaskState<void>();
  readonly onlineState: OnlineState;

  readonly onNavigationModal;

  readonly events: TypedEmitter<{
    navigation: () => ModalLayoutProps | null | undefined;
  }>;

  readonly user;

  readonly cairoSessionId: string;

  readonly biLogger;

  readonly createComponentSettings;

  readonly enableTranslatedKeySuffix: boolean;

  private _isAutoPatterns: boolean = false;

  constructor(params: Readonly<WixPatternsContainerParams>) {
    this.createEssentials = params.createEssentials;
    this.lodash = params.lodash;
    this.patchesCache = params.patchesCache;
    this.events =
      params.events ?? (new EventEmitter() as WixPatternsContainerEvents);
    this._messagesEnImport = params.messagesEnImport ?? messagesEnImportGlobal;
    this._fetchTranslations = params.fetchTranslations ?? fetchTranslations;

    this.onNavigationModal =
      params.onNavigationModal ?? new ConditionalModalState();

    this.environment = wixPatternsEnvironment(params.environment);
    this.environmentBI = params.environmentBI;
    this.user = params.user;
    this.contextType = params.contextType ?? 'SITE';

    this.createComponentSettings =
      params.createComponentSettings ?? createComponentSettings;

    this.errorMonitor = params.errorMonitor;
    this.errorHandler = params.errorHandler ?? {
      withErrorHandler: (fn) => fn(),
      getResolvedError: <T>() => {
        const { translate: t } = this;

        return {
          message: t('cairo.technical.error.text'),
        } as errorHandler.ResolvedError & Partial<T>;
      },
      showError: () => {
        const { translate: t } = this;

        this.showToast?.({
          message: t('cairo.technical.error.text'),
          type: 'ERROR',
        });
      },
    };
    this.httpClient = params.httpClient;
    this.onlineManager = params.onlineManager;
    this.focusManager = params.focusManager;
    this.history = params.history;
    this.useTranslation = params.useTranslation;
    this.useExperiments = createUseExperimentsCompat(params.useExperiments);
    this.experiments = params.experiments;
    this.showToast = params.showToast;
    this.usePageLocation = params.usePageLocation;
    this.navigateTo = params.navigateTo;
    this.onNavigation = params.onNavigation;
    this.getHostContainer = params.getHostContainer;
    this.window = params.window;
    this.queryCache = params.queryCache;
    this.appendModalsToBody = params.appendModalsToBody;
    this.layoutType = params.layoutType;
    this._useRelativeSidePanel = params.useRelativeSidePanel;
    this.onSidePanelOpen = params.onSidePanelOpen;
    this.closeSidePanel = params.closeSidePanel;
    this.onBeforeUnload = params.onBeforeUnload;

    const {
      i18n,
      biLoggerFactory,
      fedopsLogger: internalFedops,
      errorMonitor: internalErrorMonitor,
      experiments: internalExperiments,
    } = this.createEssentials({
      i18n: {
        useSuspense: false,
        disableAutoInit: true,
        asyncMessagesLoader: async (locale) => {
          if (locale === 'en') {
            return this._messagesEnImport();
          }
          return this._fetchTranslations(this)(locale);
        },
        messages: params.initialProps?.messages,
      },
      errorMonitor: {
        dsn:
          process.env.NODE_ENV === 'production'
            ? 'https://9b9586a905eb429cbda9ea49d5d721f6@sentry-next.wixpress.com/8780'
            : '',
        /*
        TODO: not used in sentry-next API, need to be merged from reportError API with tags & extraData
        For Example: errorMonitor.captureException(error, { tags, contexts: { runtime: extraData }})
        beforeSend: (event, hint) => {
          this.internalMonitor.reportErrorThrownOncePerFlow();

          if (!event.tags) {
            event.tags = {};
          }

          if (!event.extra) {
            event.extra = {};
          }

          if (hint && hint.originalException) {
            event.extra.exception = hint.originalException;

            if (hint.originalException instanceof Error) {
              event.fingerprint = [
                `${hint.originalException.name}:${hint.originalException.message}`,
              ];
            }
          }

          event.tags.componentName = this.environment.componentName;
          event.tags.componentId = this.environment.componentId;
          event.tags.artifactId = this.environment.artifactId;
          event.tags.cairoVersion = this.environment.uiLibVersion;

          return event;
        },
        */
      },
      fedops: {
        appName: 'cairo',
      },
      experiments: {
        scopes: ['cairo'],
      },
    });

    if (
      typeof process !== 'undefined' &&
      process.env.STORYBOOK_VISUAL_E2E === 'true'
    ) {
      i18n.options.fallbackLng = [];
    }

    this.cairoSessionId = uuid();
    this.biLogger = params.biLogger ?? biLoggerFactory?.().logger();
    this.i18n = i18n;
    this.internalMonitor = new WixPatternsMonitor({
      errorMonitor: params.errorMonitor,
      internalErrorMonitor,
      internalFedops,
      environment: this.environment,
      csid: this.cairoSessionId,
    });
    this.internalExperiments = internalExperiments;

    this.enableTranslatedKeySuffix =
      this.experiments.enabled('specs.cairo.example-bm.tagsToLabels') ||
      this.experiments.enabled('specs.cairo.contacts.tagsToLabels');

    this.translate = (key, params) => {
      void this.initTask.status; // status is observable so reading it here starts mobx reactivity
      if (this.enableTranslatedKeySuffix) {
        return this.i18n.t(`${key}.asLabels`, {
          ...params,
          defaultValue: this.i18n.t(key, params),
        });
      }
      return this.i18n.t(key, params);
    };

    this.Trans = params.Trans;

    this.onlineState = new OnlineState({
      onlineManager: this.onlineManager,
      focusManager: this.focusManager,
      translate: this.translate,
      showToast: this.showToast,
    });

    this.reportBi = this.createBILogger({});

    makeObservable(this, {
      init: action.bound,
      initialFetch: action.bound,
      dateFormatters: computed,
      isPanelLayout: computed,
    });
  }
  setIsAutoPatterns(value: boolean) {
    this._isAutoPatterns = value;
  }

  _createComponentSettings(options: { namespace: string }) {
    return this.createComponentSettings(this)(options);
  }

  createBILogger = <P>(defaultParams: P): ReportBI => {
    return (reportProps: ReportProps) => {
      const { environment, environmentBI, cairoSessionId, biLogger, user } =
        this;

      const {
        params: { url, ...rest },
      } = reportProps;

      return biLogger?.report({
        ...reportProps,
        params: {
          ...(environment.msid && { msid: environment.msid }),
          ...(user.id && { uuid: user.id }),
          artifactId: environment.artifactId,
          artifactIdNew: environmentBI.artifactId,
          componentName: environment.componentName,
          componentIdNew: environmentBI.componentId,
          cairoVersion: environment.uiLibVersion,
          csid: cairoSessionId,
          ...(environment.providerType === 'bm' && {
            _hostingPlatform: 'business-manager',
          }),
          isAutoPatterns: this._isAutoPatterns,
          ...defaultParams,
          ...(url && { url: fixDomainForBI(url) }),
          ...rest,
        },
      });
    };
  };

  async initialFetch() {
    const { initTask, i18n, internalExperiments, experiments } = this;

    initTask.runOnce(async () => {
      await Promise.all([
        i18n.init(),
        internalExperiments?.ready().catch((e) => {
          console.error(e);
        }),
        experiments.ready().catch((e: unknown) => {
          console.error(e);
        }),
        this._messagesEnImport(), // not all translation files contain all the keys, so en is required otherwise show error state
      ]);
    });

    return initTask.status.promise;
  }

  get dateFormatters() {
    return {
      medium: new Intl.DateTimeFormat(this.environment.language, {
        dateStyle: 'medium',
      }),
      mediumTime: new Intl.DateTimeFormat(this.environment.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      time: new Intl.DateTimeFormat(this.environment.language, {
        timeStyle: 'short',
      }),
    };
  }

  get isPanelLayout() {
    return this.layoutType === 'panel';
  }

  get useRelativeSidePanel() {
    return (
      this._useRelativeSidePanel ||
      this.internalExperiments?.enabled('specs.cairo.useRelativeSidePanel')
    );
  }

  init() {
    const { onlineState } = this;

    this.initialFetch().catch((e) => {
      console.error(e);
    });

    const disposers = [
      onlineState.init(),
      new BeforeUnloadCompatState(this).init(),
    ];

    return () => {
      disposers.forEach((d) => d?.());
    };
  }
}
